// features/workstations/dashboard-sections/pre-market.js
// ═══════════════════════════════════════════════════════════
// PRE-MARKET READ — the overnight + pre-open scan
// ═══════════════════════════════════════════════════════════
// Day-trader widget. Compact, dense, scan-in-10-seconds:
//   - US futures (ES / NQ / RTY / YM) with pre-market prints
//   - International markets (N225 / HSI / DAX / FTSE)
//   - VIX level + term-structure tag
//   - Today’s FOMC/BLS events
//   - Net bias from latest force snapshot
//   - Open positions count (if any) as a gut-check
//
// Domain-agnostic at the widget level — just renders whatever
// the pre-market API returns. Trading-specific shaping lives
// in the API (and by extension lib/intelligence/build-trading-
// bundle.js).
//
// Composed from shared primitives to keep visual language
// consistent with Today’s Read + Force Tracker.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  MetaField,
} from '../../../components/widgets/primitives';

// Direction → tone mapping for chip tints
function dirTone(dir) {
  if (dir === 'up') return 'sage';
  if (dir === 'dn' || dir === 'down') return 'terra';
  return 'steel';
}

function biasTone(bias) {
  const b = String(bias || '').toLowerCase();
  if (b === 'bullish') return 'sage';
  if (b === 'bearish') return 'terra';
  return 'steel';
}

function biasLabel(bias) {
  if (!bias) return 'neutral';
  return String(bias).toLowerCase();
}

// ═══════════════════════════════════════════════════════════
// TOP-LEVEL SECTION
// ═══════════════════════════════════════════════════════════
export default function PreMarketSection({ workstation, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/pre-market?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData(payload || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <section className="ws-premarket">
        <div className="ws-premarket-loading">Loading pre-market…</div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="ws-premarket">
        <Eyebrow>pre-market — read failed</Eyebrow>
        <SerifSummary>{error || 'No data returned.'}</SerifSummary>
      </section>
    );
  }

  return <PreMarketBody data={data} onRefresh={load} />;
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function PreMarketBody({ data }) {
  const us    = Array.isArray(data.futures_us)   ? data.futures_us   : [];
  const intl  = Array.isArray(data.futures_intl) ? data.futures_intl : [];
  const vol   = Array.isArray(data.volatility?.grid) ? data.volatility.grid : [];
  const today = Array.isArray(data.events_today) ? data.events_today : [];
  const week  = Array.isArray(data.events_week)  ? data.events_week  : [];
  const rates = Array.isArray(data.rates?.rows)   ? data.rates.rows   : [];

  const termStructure = data.volatility?.term_structure;
  const netBias = data.net_bias;
  const openCount = data.open_positions_count || 0;

  // Headline read — composed from snapshot + bias
  const headlineBias = netBias ? `Net bias: ${biasLabel(netBias)}` : 'Net bias: —';
  const summaryParts = [];
  if (us.length) {
    const leaders = us.slice(0, 2).map(f => `${f.name} ${f.change || ''}`.trim()).join(' · ');
    if (leaders) summaryParts.push(leaders);
  }
  if (termStructure) summaryParts.push(`VIX ${termStructure}`);
  if (today.length) summaryParts.push(`${today.length} event${today.length === 1 ? '' : 's'} today`);
  const summary = summaryParts.join(' · ') || 'No pre-market signals yet.';

  return (
    <section className="ws-premarket">
      <Eyebrow parts={[
        'pre-market',
        data.snapshot?.window || 'latest snapshot',
        openCount > 0 ? `${openCount} open position${openCount === 1 ? '' : 's'}` : null,
      ]} />

      <SerifHeadline>{headlineBias}</SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {/* ── US FUTURES ── */}
      {us.length > 0 && (
        <SectionStrip label="us futures" count={us.length}>
          <div className="ws-premarket-chip-row">
            {us.map((f, i) => (
              <FutureChip key={i} f={f} />
            ))}
          </div>
        </SectionStrip>
      )}

      {/* ── INTERNATIONAL ── */}
      {intl.length > 0 && (
        <SectionStrip label="overnight — international" count={intl.length}>
          <div className="ws-premarket-chip-row">
            {intl.map((f, i) => (
              <FutureChip key={i} f={f} />
            ))}
          </div>
        </SectionStrip>
      )}

      {/* ── VOLATILITY (VIX grid) ── */}
      {vol.length > 0 && (
        <SectionStrip
          label={termStructure ? `volatility · ${termStructure}` : 'volatility'}
          count={vol.length}
        >
          <PillTable>
            {vol.map((v, i) => (
              <PillRow
                key={i}
                keyText={v.label || ''}
                valueText={`${v.value || ''}${v.change ? ` · ${v.change}` : ''}`}
                tone={dirTone(v.dir)}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* ── RATES (key cuts from bundle) ── */}
      {rates.length > 0 && (
        <SectionStrip label="rates">
          <PillTable>
            {rates.map((r, i) => (
              <PillRow
                key={i}
                keyText={r.label || ''}
                valueText={r.value || ''}
                tone={r.tone || 'steel'}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* ── TODAY’S EVENTS ── */}
      {today.length > 0 && (
        <SectionStrip label="today — high impact" count={today.length}>
          <div className="ws-premarket-events">
            {today.map((e, i) => (
              <div key={i} className={`ws-premarket-event ws-premarket-event-${e.level || 'md'}`}>
                <div className="ws-premarket-event-head">
                  <span className="ws-premarket-event-time">{e.time || ''}</span>
                  <span className="ws-premarket-event-name">{e.name || ''}</span>
                </div>
                {e.meta && <div className="ws-premarket-event-meta">{e.meta}</div>}
              </div>
            ))}
          </div>
        </SectionStrip>
      )}

      {/* ── THIS WEEK ── (compact list) */}
      {week.length > 0 && (
        <SectionStrip label="this week" count={week.length}>
          <PillTable>
            {week.slice(0, 5).map((e, i) => (
              <PillRow
                key={i}
                keyText={e.time || ''}
                valueText={e.name || ''}
                tone={e.level === 'fomc' ? 'gold' : 'steel'}
              />
            ))}
          </PillTable>
        </SectionStrip>
      )}

      {/* Footer meta — bias + snapshot stamp */}
      <div className="ws-premarket-footer">
        <MetaField label="bias">
          <span className={`ws-premarket-bias ws-premarket-bias-${biasTone(netBias)}`}>
            {biasLabel(netBias)}
          </span>
        </MetaField>
        {data.snapshot && (
          <MetaField label="snapshot">
            {data.snapshot.date} · {data.snapshot.window}
          </MetaField>
        )}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// FUTURE CHIP — compact direction + magnitude tile
// ═══════════════════════════════════════════════════════════
function FutureChip({ f }) {
  const tone = dirTone(f.dir);
  return (
    <div className={`ws-premarket-chip ws-premarket-chip-${tone}`}>
      <div className="ws-premarket-chip-name">{f.name || ''}</div>
      {f.value && <div className="ws-premarket-chip-value">{f.value}</div>}
      <div className={`ws-premarket-chip-change ws-premarket-chip-change-${tone}`}>
        {f.change || ''}
      </div>
    </div>
  );
}
